/**
 * Faz 9: Toplantı — gerçek WebRTC mesh implementasyonu.
 *
 * Akış:
 *   1. Toplantı listesi (REST /meetings) — kullanıcının açtığı + davet edildiği.
 *   2. Yeni toplantı: title + invite emails → /meetings POST.
 *   3. Toplantıya gir: /meetings/{id}/join → ardından dedicated SignalingClient
 *      açar, meeting-join gönderir, meeting-peer-list dinler ve her peer için
 *      RTCPeerConnection kurar (offerer = lexicographic device-ID küçük olan).
 *   4. getUserMedia(audio+video) yerel stream'i tüm peer'lere addTrack ile attach.
 *   5. Remote track'ler tile'a render edilir (ontrack callback).
 */
import { applyStyle, escapeHtml } from '../ui/css';
import { createButton } from '../ui/components/button';
import { createPageHeader } from '../ui/components/pageHeader';
import { icons } from '../ui/icons';
import { appState, resolveDefaultSignalUrl } from '../state';
import { ensureWebControllerDevice } from '../services/web-controller-device';
import { SignalingClient } from '../services/signaling-client';
const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
];
export function renderMeeting() {
    const root = document.createElement('div');
    const view = { mode: 'list' };
    let listEl = null;
    let liveEl = null;
    function renderView() {
        root.innerHTML = '';
        if (view.mode === 'list') {
            listEl = renderList(switchToLive);
            root.append(listEl);
        }
        else {
            liveEl = renderLive(view.meetingId, switchToList);
            root.append(liveEl);
        }
    }
    function switchToLive(meetingId) {
        view.mode = 'live';
        view.meetingId = meetingId;
        renderView();
    }
    function switchToList() {
        view.mode = 'list';
        view.meetingId = undefined;
        renderView();
    }
    renderView();
    return root;
}
// ─────────────────────────────────────────────────────────────────
// LIST VIEW
// ─────────────────────────────────────────────────────────────────
function renderList(onJoin) {
    const root = document.createElement('div');
    const newBtn = createButton({
        label: 'Yeni Toplantı',
        variant: 'primary',
        iconHtml: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
        onClick: () => openCreateDialog(),
    });
    root.append(createPageHeader({
        title: 'Toplantı',
        highlight: 'Mesh',
        subtitle: 'WebRTC P2P · 4 katılımcıya kadar uçtan uca',
        rightSlot: newBtn,
    }));
    const card = document.createElement('section');
    applyStyle(card, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        minHeight: '400px',
    });
    const header = document.createElement('h3');
    applyStyle(header, { margin: '0 0 var(--sp-3) 0', fontSize: 'var(--fs-h3)', color: 'var(--text-primary)' });
    header.textContent = 'Toplantılarım';
    card.append(header);
    const status = document.createElement('div');
    applyStyle(status, { color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)', padding: 'var(--sp-3)' });
    status.textContent = 'Yükleniyor…';
    card.append(status);
    root.append(card);
    async function reload() {
        try {
            const list = await appState.backend.meetingsList();
            status.remove();
            card.querySelectorAll('.mt-row').forEach(n => n.remove());
            if (list.length === 0) {
                const empty = document.createElement('div');
                applyStyle(empty, { color: 'var(--text-tertiary)', textAlign: 'center', padding: 'var(--sp-5)' });
                empty.textContent = 'Henüz toplantı yok. "Yeni Toplantı" ile başlat.';
                empty.classList.add('mt-row');
                card.append(empty);
                return;
            }
            for (const m of list) {
                card.append(renderMeetingRow(m, onJoin, reload));
            }
        }
        catch (e) {
            status.textContent = `Hata: ${e}`;
        }
    }
    function openCreateDialog() {
        const dialog = createDialog();
        const title = document.createElement('input');
        title.placeholder = 'Toplantı adı';
        applyStyle(title, dialogInputStyle());
        const invites = document.createElement('input');
        invites.placeholder = 'Davet e-postaları (virgülle ayır)';
        applyStyle(invites, dialogInputStyle());
        const errEl = document.createElement('div');
        applyStyle(errEl, { color: 'var(--status-error)', fontSize: 'var(--fs-caption)', minHeight: '20px' });
        const create = createButton({
            label: 'Oluştur',
            variant: 'primary',
            onClick: async () => {
                if (!title.value.trim()) {
                    errEl.textContent = 'Toplantı adı gerekli';
                    return;
                }
                try {
                    const emails = invites.value.split(',').map(e => e.trim()).filter(Boolean);
                    await appState.backend.meetingsCreate(title.value.trim(), emails.length > 0 ? emails : undefined);
                    dialog.close();
                    await reload();
                }
                catch (e) {
                    errEl.textContent = String(e);
                }
            },
        });
        const cancel = createButton({
            label: 'İptal', variant: 'ghost',
            onClick: () => dialog.close(),
        });
        dialog.body.append(title, invites, errEl);
        dialog.footer.append(cancel, create);
        dialog.open();
    }
    void reload();
    return root;
}
function renderMeetingRow(m, onJoin, onChange) {
    const row = document.createElement('div');
    row.classList.add('mt-row', 'pr-meeting-row');
    applyStyle(row, {
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderTop: '1px solid var(--border-subtle)',
    });
    const main = document.createElement('div');
    const ownerLabel = m.ownerUserId === parseUserIdFromJwt() ? 'Sen' : m.ownerEmail;
    main.innerHTML = `
    <div style="font-size:var(--fs-base);color:var(--text-primary);font-weight:500">${escapeHtml(m.title)}</div>
    <div style="font-size:var(--fs-caption);color:var(--text-tertiary)">
      ${escapeHtml(ownerLabel)} · ${escapeHtml(m.participants.length + ' katılımcı')} · ${escapeHtml(formatStatus(m.status))}
    </div>
  `;
    row.append(main);
    const time = document.createElement('div');
    applyStyle(time, { fontSize: 'var(--fs-caption)', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' });
    time.textContent = new Date(m.createdAt).toLocaleString('tr-TR');
    row.append(time);
    const join = createButton({
        label: m.status === 'ended' ? 'Bitti' : 'Katıl',
        variant: 'primary', size: 'sm',
        onClick: async () => {
            if (m.status === 'ended')
                return;
            try {
                await appState.backend.meetingJoin(m.id);
                onJoin(m.id);
            }
            catch (e) {
                alert(`Katılım hatası: ${e}`);
            }
        },
    });
    if (m.status === 'ended')
        join.disabled = true;
    row.append(join);
    const isOwner = m.ownerUserId === parseUserIdFromJwt();
    const action = isOwner && m.status !== 'ended'
        ? createButton({
            label: 'Bitir', variant: 'ghost', size: 'sm',
            onClick: async () => {
                await appState.backend.meetingEnd(m.id);
                onChange();
            },
        })
        : document.createElement('span');
    row.append(action);
    return row;
}
function formatStatus(s) {
    switch (s) {
        case 'scheduled': return 'planlandı';
        case 'live': return 'CANLI';
        case 'ended': return 'sona erdi';
        default: return s;
    }
}
function parseUserIdFromJwt() {
    const t = appState.accessToken;
    if (!t)
        return '';
    try {
        const parts = t.split('.');
        if (parts.length < 2)
            return '';
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.sub ?? '';
    }
    catch {
        return '';
    }
}
// ─────────────────────────────────────────────────────────────────
// LIVE VIEW (WebRTC mesh)
// ─────────────────────────────────────────────────────────────────
function renderLive(meetingId, onLeave) {
    const root = document.createElement('div');
    let signaling = null;
    let localStream = null;
    let myDeviceId = '';
    let myDisplayName = appState.userEmail ?? 'Sen';
    let mediaStarted = false;
    const peers = new Map();
    // ── Header ──
    const leaveBtn = createButton({
        label: 'Ayrıl', variant: 'danger',
        iconHtml: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>',
        onClick: () => {
            void teardown();
            onLeave();
        },
    });
    root.append(createPageHeader({
        title: 'Toplantı',
        highlight: 'Canlı',
        subtitle: `Mesh ID ${meetingId.substring(0, 8)} · WebRTC P2P`,
        rightSlot: leaveBtn,
    }));
    // ── Tile grid ──
    const grid = document.createElement('div');
    grid.classList.add('pr-meeting-grid');
    applyStyle(grid, {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 'var(--sp-3)',
        minHeight: '420px',
    });
    root.append(grid);
    // local tile
    const localTile = makeTile(myDisplayName, true);
    grid.append(localTile.el);
    // ── Toolbar (mic/cam toggle) ──
    const toolbar = document.createElement('div');
    applyStyle(toolbar, {
        display: 'flex', justifyContent: 'center', gap: 'var(--sp-3)',
        marginTop: 'var(--sp-3)', padding: 'var(--sp-3)',
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
    });
    let micOn = true;
    let camOn = true;
    const micBtn = createButton({
        label: 'Mikrofon', variant: 'primary',
        onClick: () => {
            micOn = !micOn;
            localStream?.getAudioTracks().forEach(t => (t.enabled = micOn));
            micBtn.dataset.variant = micOn ? 'primary' : 'ghost';
            micBtn.textContent = micOn ? 'Mikrofon Açık' : 'Mikrofon Kapalı';
        },
    });
    micBtn.textContent = 'Mikrofon Açık';
    const camBtn = createButton({
        label: 'Kamera', variant: 'primary',
        onClick: () => {
            camOn = !camOn;
            localStream?.getVideoTracks().forEach(t => (t.enabled = camOn));
            camBtn.textContent = camOn ? 'Kamera Açık' : 'Kamera Kapalı';
        },
    });
    camBtn.textContent = 'Kamera Açık';
    // Faz 9: ekran paylaşma — getDisplayMedia + replaceTrack pattern.
    // Toggle açıkken kamera videosu screen track ile değişir; tekrar basınca
    // kamera track'i geri konur. Tarayıcı native picker tüm monitor + pencere +
    // sekme seçimini gösterir.
    let screenStream = null;
    let screenOn = false;
    const screenBtn = createButton({
        label: 'Ekran Paylaş', variant: 'ghost',
        onClick: async () => {
            try {
                if (!screenOn) {
                    screenStream = await navigator.mediaDevices.getDisplayMedia({
                        video: { frameRate: 30 },
                        audio: false,
                    });
                    // Replace each peer connection's outbound video track
                    const screenTrack = screenStream.getVideoTracks()[0];
                    for (const conn of peers.values()) {
                        const sender = conn.pc.getSenders().find(s => s.track && s.track.kind === 'video');
                        if (sender)
                            await sender.replaceTrack(screenTrack);
                    }
                    // Self preview de ekrana geçsin
                    if (localTile.video)
                        localTile.video.srcObject = screenStream;
                    // Kullanıcı "stop sharing" tarayıcıdan basarsa otomatik geri dön
                    screenTrack.onended = () => {
                        screenOn = true; // toggle handler revert için
                        void screenBtn.dispatchEvent(new MouseEvent('click'));
                    };
                    screenOn = true;
                    screenBtn.textContent = 'Paylaşmayı Durdur';
                    screenBtn.dataset.variant = 'primary';
                }
                else {
                    // Stop screen, restore camera
                    screenStream?.getTracks().forEach(t => t.stop());
                    screenStream = null;
                    const camTrack = localStream?.getVideoTracks()[0];
                    if (camTrack) {
                        for (const conn of peers.values()) {
                            const sender = conn.pc.getSenders().find(s => s.track && s.track.kind === 'video');
                            if (sender)
                                await sender.replaceTrack(camTrack);
                        }
                    }
                    if (localTile.video)
                        localTile.video.srcObject = localStream;
                    screenOn = false;
                    screenBtn.textContent = 'Ekran Paylaş';
                    screenBtn.dataset.variant = 'ghost';
                }
            }
            catch (e) {
                status.textContent = `Ekran paylaşma hata: ${e}`;
            }
        },
    });
    screenBtn.textContent = 'Ekran Paylaş';
    toolbar.append(micBtn, camBtn, screenBtn);
    root.append(toolbar);
    const status = document.createElement('div');
    applyStyle(status, {
        marginTop: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)',
        fontSize: 'var(--fs-caption)', color: 'var(--text-tertiary)',
    });
    status.textContent = 'Hazırlanıyor…';
    root.append(status);
    // ── Setup ──
    void start();
    async function start() {
        try {
            const dev = await ensureWebControllerDevice();
            myDeviceId = dev.deviceId;
            const token = appState.accessToken;
            if (!token)
                throw new Error('access token yok');
            try {
                localStream = await navigator.mediaDevices.getUserMedia({
                    audio: true, video: { width: 1280, height: 720 },
                });
                localTile.video.srcObject = localStream;
                mediaStarted = true;
            }
            catch (e) {
                status.textContent = `Kamera/mikrofon erişimi reddedildi: ${e}`;
            }
            const wsUrl = resolveDefaultSignalUrl();
            const sig = new SignalingClient();
            signaling = sig;
            sig.setOnMessage(handleSignalMessage);
            await sig.connect(wsUrl, token);
            sig.register(myDeviceId, '', ['meeting', 'v1']);
            sig.send({
                type: 'meeting-join',
                meetingId,
                peerDeviceId: myDeviceId,
                peerDisplayName: myDisplayName,
            });
            status.textContent = 'Bağlandı; peer bekleniyor…';
        }
        catch (e) {
            status.textContent = `Hata: ${e}`;
        }
    }
    async function handleSignalMessage(msg) {
        try {
            if (msg.type === 'meeting-peer-list' && msg.meetingId === meetingId) {
                const peers_ = msg.peers;
                await reconcilePeers(peers_);
            }
            else if (msg.type === 'webrtc-offer' && msg.meetingId === meetingId
                && msg.toDeviceId === myDeviceId) {
                await handleOffer(msg.fromDeviceId, msg.sdp);
            }
            else if (msg.type === 'webrtc-answer' && msg.meetingId === meetingId
                && msg.toDeviceId === myDeviceId) {
                await handleAnswer(msg.fromDeviceId, msg.sdp);
            }
            else if (msg.type === 'webrtc-ice' && msg.meetingId === meetingId
                && msg.toDeviceId === myDeviceId) {
                await handleIce(msg.fromDeviceId, msg.candidateJson);
            }
        }
        catch (e) {
            console.error('meeting signal handle error', e);
        }
    }
    async function reconcilePeers(list) {
        const seen = new Set();
        for (const p of list) {
            if (p.deviceId === myDeviceId)
                continue;
            seen.add(p.deviceId);
            if (!peers.has(p.deviceId)) {
                await createPeer(p.deviceId, p.displayName, /*isOfferer*/ myDeviceId < p.deviceId);
            }
        }
        // Ayrılanları kaldır
        for (const id of Array.from(peers.keys())) {
            if (!seen.has(id)) {
                const pc = peers.get(id);
                if (pc) {
                    pc.pc.close();
                    pc.tileEl.remove();
                    peers.delete(id);
                }
            }
        }
        status.textContent = `${peers.size + 1} katılımcı`;
    }
    async function createPeer(deviceId, displayName, isOfferer) {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        const remoteStream = new MediaStream();
        const tile = makeTile(displayName, false);
        grid.append(tile.el);
        tile.video.srcObject = remoteStream;
        const conn = {
            deviceId, displayName, pc,
            tileEl: tile.el, remoteStream,
            iceQueue: [], remoteSet: false,
        };
        peers.set(deviceId, conn);
        if (localStream) {
            for (const track of localStream.getTracks()) {
                pc.addTrack(track, localStream);
            }
        }
        pc.ontrack = (ev) => {
            ev.streams[0]?.getTracks().forEach(t => remoteStream.addTrack(t));
        };
        pc.onicecandidate = (ev) => {
            if (ev.candidate && signaling) {
                signaling.send({
                    type: 'webrtc-ice',
                    meetingId,
                    fromDeviceId: myDeviceId,
                    toDeviceId: deviceId,
                    candidateJson: JSON.stringify(ev.candidate.toJSON()),
                });
            }
        };
        if (isOfferer) {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            signaling?.send({
                type: 'webrtc-offer',
                meetingId,
                fromDeviceId: myDeviceId,
                toDeviceId: deviceId,
                sdp: offer.sdp ?? '',
            });
        }
    }
    async function handleOffer(fromDeviceId, sdp) {
        let conn = peers.get(fromDeviceId);
        if (!conn) {
            // Self yet to see in peer-list — proactively create
            await createPeer(fromDeviceId, '(katılımcı)', false);
            conn = peers.get(fromDeviceId);
        }
        if (!conn)
            return;
        await conn.pc.setRemoteDescription({ type: 'offer', sdp });
        conn.remoteSet = true;
        for (const c of conn.iceQueue) {
            try {
                await conn.pc.addIceCandidate(c);
            }
            catch { }
        }
        conn.iceQueue = [];
        const answer = await conn.pc.createAnswer();
        await conn.pc.setLocalDescription(answer);
        signaling?.send({
            type: 'webrtc-answer',
            meetingId,
            fromDeviceId: myDeviceId,
            toDeviceId: fromDeviceId,
            sdp: answer.sdp ?? '',
        });
    }
    async function handleAnswer(fromDeviceId, sdp) {
        const conn = peers.get(fromDeviceId);
        if (!conn)
            return;
        await conn.pc.setRemoteDescription({ type: 'answer', sdp });
        conn.remoteSet = true;
        for (const c of conn.iceQueue) {
            try {
                await conn.pc.addIceCandidate(c);
            }
            catch { }
        }
        conn.iceQueue = [];
    }
    async function handleIce(fromDeviceId, candidateJson) {
        const conn = peers.get(fromDeviceId);
        if (!conn)
            return;
        const c = JSON.parse(candidateJson);
        if (!conn.remoteSet) {
            conn.iceQueue.push(c);
            return;
        }
        try {
            await conn.pc.addIceCandidate(c);
        }
        catch { }
    }
    async function teardown() {
        try {
            signaling?.send({
                type: 'meeting-leave',
                meetingId,
                peerDeviceId: myDeviceId,
            });
        }
        catch { }
        try {
            await appState.backend.meetingLeave(meetingId);
        }
        catch { }
        for (const c of peers.values()) {
            try {
                c.pc.close();
            }
            catch { }
        }
        peers.clear();
        if (localStream) {
            localStream.getTracks().forEach(t => t.stop());
            localStream = null;
        }
        try {
            signaling?.close();
        }
        catch { }
    }
    // Cleanup on unmount
    const observer = new MutationObserver(() => {
        if (!document.body.contains(root)) {
            void teardown();
            observer.disconnect();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    void mediaStarted; // keep
    void icons; // unused but prevent tree-shake regression
    return root;
}
function makeTile(displayName, isLocal) {
    const tile = document.createElement('div');
    applyStyle(tile, {
        background: '#000',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        aspectRatio: '16 / 9',
        position: 'relative',
    });
    const video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.muted = isLocal; // local muted to prevent echo
    applyStyle(video, { width: '100%', height: '100%', objectFit: 'cover' });
    tile.append(video);
    const label = document.createElement('div');
    applyStyle(label, {
        position: 'absolute', left: '8px', bottom: '8px',
        background: 'rgba(0,0,0,0.55)', color: 'white',
        fontSize: 'var(--fs-tiny)', padding: '2px 8px',
        borderRadius: '6px', fontFamily: 'var(--font-mono)',
    });
    label.textContent = isLocal ? `${displayName} (siz)` : displayName;
    tile.append(label);
    return { el: tile, video };
}
function createDialog() {
    const overlay = document.createElement('div');
    applyStyle(overlay, {
        position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: '1000',
    });
    const card = document.createElement('div');
    applyStyle(card, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        minWidth: '380px',
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
    });
    const body = document.createElement('div');
    applyStyle(body, { display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' });
    const footer = document.createElement('div');
    applyStyle(footer, { display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' });
    card.append(body, footer);
    overlay.append(card);
    return {
        body, footer,
        open() { document.body.append(overlay); },
        close() { overlay.remove(); },
    };
}
function dialogInputStyle() {
    return {
        background: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm)',
        padding: '8px 12px',
        color: 'var(--text-primary)',
        fontSize: 'var(--fs-base)',
        outline: 'none',
    };
}
